import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "container-fluid classifier-panel" }
const _hoisted_2 = { class: "row mb-3" }
const _hoisted_3 = { class: "col-12 d-flex justify-content-between align-items-center flex-wrap gap-2" }
const _hoisted_4 = { class: "panel-title mb-0" }
const _hoisted_5 = {
  key: 0,
  class: "d-flex align-items-center gap-2"
}
const _hoisted_6 = {
  class: "text-muted small mb-0",
  for: "classifier-days"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["value"]
const _hoisted_9 = {
  class: "text-muted small mb-0",
  for: "classifier-since"
}
const _hoisted_10 = { class: "card bg-dark text-light mb-3" }
const _hoisted_11 = { class: "card-body" }
const _hoisted_12 = { class: "row mb-0 small" }
const _hoisted_13 = { class: "col-sm-3" }
const _hoisted_14 = { class: "col-sm-9 text-break" }
const _hoisted_15 = { class: "col-sm-3" }
const _hoisted_16 = { class: "col-sm-9" }
const _hoisted_17 = { class: "col-sm-3" }
const _hoisted_18 = { class: "col-sm-9" }
const _hoisted_19 = { class: "col-sm-3" }
const _hoisted_20 = { class: "col-sm-9" }
const _hoisted_21 = { class: "col-sm-3" }
const _hoisted_22 = { class: "col-sm-9" }
const _hoisted_23 = { class: "col-sm-3" }
const _hoisted_24 = { class: "col-sm-9" }
const _hoisted_25 = {
  key: 0,
  class: "text-warning small"
}
const _hoisted_26 = { class: "mb-2" }
const _hoisted_27 = { class: "text-muted small" }
const _hoisted_28 = { class: "table-responsive mb-4" }
const _hoisted_29 = { class: "table table-sm align-middle" }
const _hoisted_30 = { class: "mb-2" }
const _hoisted_31 = { class: "table-responsive" }
const _hoisted_32 = { class: "table table-sm align-middle" }

import { computed, onMounted, ref } from "vue";
import { apiGet } from "@/services/api/client";
import { formatIsoDate } from "@/utils/format";
import StateBlock from "@/components/ui/StateBlock.vue";
import PanelNoAccess from "@/components/panel/PanelNoAccess.vue";
import { panelRequest, isForbidden, reportPanelError } from "@/components/panel/panelAccess";

/** One column: header key under panel.classifier.col and how to print it from a summary / daily row. */
interface Col { key: string; fmt: (s: any) => string }


export default /*@__PURE__*/_defineComponent({
  __name: 'ClassifierComponent',
  setup(__props) {

const forbidden = ref(false);
const loading = ref(true);
const failed = ref(false);
const days = ref(14);
/** YYYY-MM-DD the flag went on; when set, "after" starts there and "before" is the same length */
const since = ref("");
const status = ref<any>({});
const impact = ref<any>({});

const types = ["support", "report"] as const;
const periodsList = ["before", "after"] as const;

const num = (v: unknown) => (typeof v === "number" ? String(Math.round(v * 1000) / 1000) : "-");
const pct = (v: unknown) => (typeof v === "number" ? `${Math.round(v * 1000) / 10}%` : "-");
const mins = (v: unknown) => (typeof v === "number" ? `${v} min` : "-");
const src = (s: any) => (s?.bySource ? `${s.bySource.auto} / ${s.bySource.fallback} / ${s.bySource.player} / ${s.bySource.none}` : "-");

const summaryCols: Col[] = [
  { key: "total", fmt: s => String(s?.total ?? "-") },
  { key: "catchAllShare", fmt: s => pct(s?.catchAllShare) },
  { key: "withDecision", fmt: s => String(s?.withDecision ?? "-") },
  { key: "bySource", fmt: src },
  { key: "overrideRate", fmt: s => pct(s?.overrideRate) },
  { key: "autoShare", fmt: s => pct(s?.autoShare) },
  { key: "medianFirstResponse", fmt: s => mins(s?.medianFirstResponseMin) },
  { key: "p90FirstResponse", fmt: s => mins(s?.p90FirstResponseMin) },
  { key: "medianClose", fmt: s => mins(s?.medianCloseMin) },
];
const dailyCols: Col[] = [
  { key: "total", fmt: s => String(s.total) },
  { key: "catchAllShare", fmt: s => pct(s.catchAllShare) },
  { key: "withDecision", fmt: s => String(s.withDecision) },
  { key: "bySource", fmt: src },
  { key: "overrideRate", fmt: s => pct(s.overrideRate) },
  { key: "medianFirstResponse", fmt: s => mins(s.medianFirstResponseMin) },
  { key: "medianClose", fmt: s => mins(s.medianCloseMin) },
];

const activeMetrics = computed(() => (status.value.models || []).find((m: any) => String(m._id) === status.value.activeModelId)?.metrics || {});
const flagList = computed(() => ["simpleTicketFlow", "simpleReportFlow", "categorySuggestions"].map(key => ({ key, on: impact.value.flags?.[key] === true })));
// newest day first reads better than the oldest-first order the server returns
const daily = computed<any[]>(() => [...(impact.value.daily || [])].reverse());

async function loadImpact() {
  try {
    const query = since.value ? `since=${encodeURIComponent(since.value)}` : `days=${days.value}&before=${days.value}`;
    impact.value = await apiGet(`/admin/classifier/impact?${query}`, panelRequest);
    failed.value = false;
  } catch (error) {
    if (isForbidden(error)) forbidden.value = true;
    else { failed.value = true; reportPanelError("Error loading classifier impact:", error); }
  }
}

onMounted(async () => {
  try {
    status.value = await apiGet("/admin/classifier/status", panelRequest);
    await loadImpact();
  } catch (error) {
    if (isForbidden(error)) forbidden.value = true;
    else { failed.value = true; reportPanelError("Error loading classifier status:", error); }
  } finally {
    loading.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h3", _hoisted_4, _toDisplayString(_ctx.$t('panel.classifier.title')), 1),
        (!forbidden.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('panel.classifier.window')), 1),
              _withDirectives(_createElementVNode("select", {
                id: "classifier-days",
                class: "form-select form-select-sm w-auto",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((days).value = $event)),
                disabled: !!since.value,
                onChange: loadImpact
              }, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList([7, 14, 30], (d) => {
                  return _createElementVNode("option", {
                    key: d,
                    value: d
                  }, _toDisplayString(_ctx.$t('panel.classifier.days', { n: d })), 9, _hoisted_8)
                }), 64))
              ], 40, _hoisted_7), [
                [
                  _vModelSelect,
                  days.value,
                  void 0,
                  { number: true }
                ]
              ]),
              _createElementVNode("label", _hoisted_9, _toDisplayString(_ctx.$t('panel.classifier.since')), 1),
              _withDirectives(_createElementVNode("input", {
                id: "classifier-since",
                type: "date",
                class: "form-control form-control-sm w-auto",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((since).value = $event)),
                onChange: loadImpact
              }, null, 544), [
                [_vModelText, since.value]
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (forbidden.value)
      ? (_openBlock(), _createBlock(PanelNoAccess, { key: 0 }))
      : (_openBlock(), _createBlock(StateBlock, {
          key: 1,
          loading: loading.value,
          error: failed.value ? _ctx.$t('panel.classifier.loadFailed') : false
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("dl", _hoisted_12, [
                  _createElementVNode("dt", _hoisted_13, _toDisplayString(_ctx.$t('panel.classifier.model')), 1),
                  _createElementVNode("dd", _hoisted_14, _toDisplayString(status.value.activeModelId || _ctx.$t('panel.classifier.noModel')), 1),
                  _createElementVNode("dt", _hoisted_15, _toDisplayString(_ctx.$t('panel.classifier.trainedAt')), 1),
                  _createElementVNode("dd", _hoisted_16, _toDisplayString(status.value.activeModelCreatedAt ? _unref(formatIsoDate)(status.value.activeModelCreatedAt) : '-'), 1),
                  _createElementVNode("dt", _hoisted_17, _toDisplayString(_ctx.$t('panel.classifier.accuracy')), 1),
                  _createElementVNode("dd", _hoisted_18, _toDisplayString(pct(activeMetrics.value.acc1)) + " / " + _toDisplayString(pct(activeMetrics.value.acc3)), 1),
                  _createElementVNode("dt", _hoisted_19, _toDisplayString(_ctx.$t('panel.classifier.labels')), 1),
                  _createElementVNode("dd", _hoisted_20, _toDisplayString(status.value.labels ?? 0), 1),
                  _createElementVNode("dt", _hoisted_21, _toDisplayString(_ctx.$t('panel.classifier.threshold')), 1),
                  _createElementVNode("dd", _hoisted_22, _toDisplayString(num(status.value.threshold?.value)) + " (" + _toDisplayString(status.value.threshold?.mode || '-') + ") · " + _toDisplayString(_ctx.$t('panel.classifier.effectiveThreshold')) + ": " + _toDisplayString(num(status.value.effectiveThreshold)), 1),
                  _createElementVNode("dt", _hoisted_23, _toDisplayString(_ctx.$t('panel.classifier.flags')), 1),
                  _createElementVNode("dd", _hoisted_24, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(flagList.value, (f) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: f.key,
                        class: _normalizeClass(["badge me-1", f.on ? 'bg-success' : 'bg-secondary'])
                      }, _toDisplayString(_ctx.$t('panel.classifier.flag.' + f.key)) + ": " + _toDisplayString(f.on ? _ctx.$t('panel.classifier.on') : _ctx.$t('panel.classifier.off')), 3))
                    }), 128))
                  ])
                ])
              ])
            ]),
            (impact.value.truncated?.before || impact.value.truncated?.after)
              ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(_ctx.$t('panel.classifier.truncated')), 1))
              : _createCommentVNode("", true),
            _createElementVNode("h5", _hoisted_26, _toDisplayString(_ctx.$t('panel.classifier.impactTitle')), 1),
            _createElementVNode("p", _hoisted_27, _toDisplayString(_ctx.$t('panel.classifier.impactHelp', { days: impact.value.days ?? days.value })), 1),
            _createElementVNode("div", _hoisted_28, [
              _createElementVNode("table", _hoisted_29, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", null, _toDisplayString(_ctx.$t('panel.classifier.type')), 1),
                    _createElementVNode("th", null, _toDisplayString(_ctx.$t('panel.classifier.period')), 1),
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(summaryCols, (c) => {
                      return _createElementVNode("th", {
                        key: c.key
                      }, _toDisplayString(_ctx.$t('panel.classifier.col.' + c.key)), 1)
                    }), 64))
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(types, (type) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: type }, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(periodsList, (period) => {
                        return _createElementVNode("tr", {
                          key: type + period
                        }, [
                          _createElementVNode("td", null, _toDisplayString(_ctx.$t('panel.classifier.types.' + type)), 1),
                          _createElementVNode("td", null, _toDisplayString(_ctx.$t('panel.classifier.periods.' + period)), 1),
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(summaryCols, (c) => {
                            return _createElementVNode("td", {
                              key: c.key
                            }, _toDisplayString(c.fmt(impact.value.periods?.[period]?.[type])), 1)
                          }), 64))
                        ])
                      }), 64))
                    ], 64))
                  }), 64))
                ])
              ])
            ]),
            _createElementVNode("h5", _hoisted_30, _toDisplayString(_ctx.$t('panel.classifier.dailyTitle')), 1),
            _createVNode(StateBlock, {
              empty: !daily.value.length,
              emptyText: _ctx.$t('panel.classifier.noTickets')
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_31, [
                  _createElementVNode("table", _hoisted_32, [
                    _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _createElementVNode("th", null, _toDisplayString(_ctx.$t('panel.classifier.day')), 1),
                        _createElementVNode("th", null, _toDisplayString(_ctx.$t('panel.classifier.type')), 1),
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(dailyCols, (c) => {
                          return _createElementVNode("th", {
                            key: c.key
                          }, _toDisplayString(_ctx.$t('panel.classifier.col.' + c.key)), 1)
                        }), 64))
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(daily.value, (row) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: row.day + row.type
                        }, [
                          _createElementVNode("td", null, _toDisplayString(row.day), 1),
                          _createElementVNode("td", null, _toDisplayString(_ctx.$t('panel.classifier.types.' + row.type)), 1),
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(dailyCols, (c) => {
                            return _createElementVNode("td", {
                              key: c.key
                            }, _toDisplayString(c.fmt(row)), 1)
                          }), 64))
                        ]))
                      }), 128))
                    ])
                  ])
                ])
              ]),
              _: 1
            }, 8, ["empty", "emptyText"])
          ]),
          _: 1
        }, 8, ["loading", "error"]))
  ]))
}
}

})